import { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react"
import { useDispatch, useSelector} from "react-redux"
import { httpFetchSavePayeeCatalog, httpFetchSavePayeeProductsForPayeeCatalog, toggleLoading, fetchBatchCreateTicketsForPayeeCatalog, fetchSubscribeToLastTicketNumberForPayeeCatalog } from "../../actions"
import { alphabeticalSort } from "../../utils/stringUtils"
import { handleChange } from "../../components/Form"
import FormField from "../../components/FormField"
import TicketCard from "../../components/TicketCard"
import TicketPrintPages from "../../components/TicketPrintPages"
import TicketDesigner from "../../components/TicketDesigner"
import Modal from "../../components/Modal"
import {PAYEE_PRODUCT_TYPE_TICKET} from "../../constants/payeeProducts"

import ProductRadioListItem from "../../components/ProductRadioListItem"
import Loading from "../../components/Loading";
import ErrorBoundary from "../../components/ErrorBoundary";

import {generateTicketsFromProductVariant} from "../../utils/ticketUtils"
import { timestampToDateString } from "../../utils/datetimeUtils"
import { OBJECT_TYPE_TICKET } from "../../constants/ticket";
import { logError } from "../../utils/errorHandlingUtils"

import { retryLazy } from "../../utils/applicationUtils";

const TicketPdfDownloadModal = lazy(() =>
    retryLazy(() => import("../../containers/TicketPdfDownloadButton/TicketPdfDownloadModal"))
);

const PhysicalTicketGeneratorForm = ({
    payeeCatalogId="",
    payeeId="",
    title="",
    onSuccess=()=>{}
}) => {
    const [errors, setErrors] = useState({})
    const [showTicketDownloadModal, setShowTicketDownloadModal] = useState(false)
    const payeeCatalog = useSelector(state => state.payeeCatalogs.payeeCatalogsById[payeeCatalogId])
    const {name:catalogName=""} = payeeCatalog || {}
    const payeeProductsById = useSelector(state => state.payeeProducts.payeeProductsById)
    const dispatch = useDispatch()
    useEffect(() => {
        //if the payee catalog is not loaded, load it and it's products
        if (payeeCatalogId){
            if (!payeeCatalog) dispatch(httpFetchSavePayeeCatalog(payeeCatalogId))
            dispatch(httpFetchSavePayeeProductsForPayeeCatalog(payeeCatalogId))
        }
    }, [payeeCatalogId, payeeCatalog])
    const ticketProductList = useMemo(() => {
        return Object.values(payeeProductsById)
                    .filter(p => p.productType === PAYEE_PRODUCT_TYPE_TICKET && 
                                p.payeeCatalogIds && 
                                p.payeeCatalogIds.includes(payeeCatalogId)
                                //remove inactive or deleted products
                                && !(p.isInactive || p.deleted) 
                            )
                    .sort((pA, pB) => alphabeticalSort(pA.name, pB.name))
    }, 
    [payeeProductsById, payeeCatalog])
    //map variants to product ids for o(1) lookup
    const productIdByProductVariantId = useMemo(() => {
        return Object.values(payeeProductsById)
                      .reduce((map, p) => {
                          Object.keys(p.variantsById).forEach(
                              variantId => map[variantId] = p.id   
                          )
                        return map
                      }, {})    
    }, [payeeProductsById])
    const [selectedProductVariantId, setSelectedVariantProductId] = useState("")
    //if there is no selected product variant, but there are ticket products, 
    //then select the default varaint of the first available ticket product 
    useEffect(() => {
        if (!selectedProductVariantId && ticketProductList.length > 0){
            setSelectedVariantProductId(ticketProductList[0].defaultVariantId)
        }
    }, [selectedProductVariantId, ticketProductList])
    //the number of tickets to generate
    const [numToGenerate, setNumToGenerate] = useState(0)
    //the last ticket number, used for numbering tickets
    const [lastTicketNumber, setLastTicketNumber] = useState(0)
    const [lastTicketNumberListener, setLastTicketNumberListener] = useState(() => () => {})
    const listenToLastTicketNumber = async () => {
     const newLastTicketNumberListener = await dispatch(fetchSubscribeToLastTicketNumberForPayeeCatalog(
            payeeCatalogId,
            payeeId,
            setLastTicketNumber,
            () => setLastTicketNumber(0)
        ))
        setLastTicketNumberListener(() => newLastTicketNumberListener)
    }
    useEffect(() => {
        if (payeeCatalog) listenToLastTicketNumber()
        return () => {
            if (typeof lastTicketNumberListener === "function") lastTicketNumberListener()
        }
    }, [payeeCatalogId]);
    //the tickets that have been generated
    const [generatedTickets, setGeneratedTickets] = useState([])
    //the configurable style of the ticket
    const [ticketStyle, setTicketStyle] = useState({})
    const selectedProduct = payeeProductsById[
        productIdByProductVariantId[selectedProductVariantId]
    ]
    const {name:productName="", variantsById={}} = selectedProduct || {}
    const {variantDetails=""} = variantsById[selectedProductVariantId] || {}
    //create one "display ticket" whenever the selected product changes
    const displayTicket = useMemo(() => {
        if (selectedProduct){
            const genDisplayList = generateTicketsFromProductVariant(
                selectedProduct,
                selectedProductVariantId,
                1,
                lastTicketNumber,
                payeeCatalogId
            )
            if (genDisplayList && genDisplayList.length > 0) return genDisplayList[0]
        }
        return null
    }, [selectedProduct, selectedProductVariantId, lastTicketNumber])
    //download a pdf of the tickets
    const ticketFilename = `${numToGenerate} Tickets for ${productName}${displayTicket && displayTicket.variantDetails ? ` (${displayTicket.variantDetails})`: ""} - ${timestampToDateString(Date.now(), "EEE, dd MMMM yy 'at' t")}.pdf`
    //validate the form
    const validate = () => {
        const errors = {}
        if (!selectedProductVariantId) errors.variantId = "Choose a product"
        if (numToGenerate <= 0 || numToGenerate > 1000) errors.numToGenerate = "Choose between 1 and 1000 tickets"
        
        if (Object.keys(errors).length === 0) return true
        else {
            setErrors(errors)
            return false
        } 
    }
    //generate the tickets
    const handleGenerate = () => {
        if (validate()){
            const generatedTickets = generateTicketsFromProductVariant(
                selectedProduct,
                selectedProductVariantId,
                numToGenerate,
                lastTicketNumber,
                payeeCatalogId
            )
            setGeneratedTickets(generatedTickets)
        }
    }
    const savingRef = useRef(false)
    const savedRef = useRef(false)
    useEffect(() => {
        if (
            generatedTickets.length > 0 &&
            generatedTickets[0].ticketNumber !== (lastTicketNumber + 1) &&
            !savingRef.current && 
            !savedRef.current//it is not due to the tickets we just created
        ) {
            //regenerate the tickets
            console.warn("Ticket number mismatch; regenerating")
            handleGenerate()
        }
    }, 
    [lastTicketNumber, generatedTickets])
    if (!payeeCatalog) return null
    const {imageUrl:selectedImageUrl=""} = selectedProduct || {}
    
    const handleSaveTickets = async () => {
        //save each ticket in the db
        //generate the QR codes for each ticket
        if (generatedTickets.length <= 0) return 
        const {batchId} = generatedTickets[0]
        dispatch(toggleLoading(true))
        if (!savedRef.current){
            try {
                savingRef.current = true
                const ticketCreationSuccessful = await dispatch(fetchBatchCreateTicketsForPayeeCatalog(
                    generatedTickets,
                    batchId,
                    payeeCatalogId,
                    payeeId,
                    generatedTickets.length,
                    generatedTickets[0].ticketNumber,
                    generatedTickets[generatedTickets.length -1].ticketNumber,
                    ticketStyle,
                ))
                if (!ticketCreationSuccessful) throw new Error(`Failed to save tickets in database. Please check your internet connection`)
            } catch(error){
                alert(error.message)
                logError(`${error.message}. payee: ${payeeId} payeeCatalogId: ${payeeCatalogId}`)
                dispatch(toggleLoading(false))
                return
            } finally {
                savingRef.current = false
            }
        }
        savedRef.current = true
        let ticketsWithQRDataUrls
        try {
            const { getQrCodeDataUrl } = await import("../../containers/TicketPdfDownloadButton/qrUtils");
            await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
            ticketsWithQRDataUrls = await Promise.all(
                generatedTickets.map(async (ticket) => {
                const qrDataUrl = await getQrCodeDataUrl(ticket, OBJECT_TYPE_TICKET);
                return { ...ticket, qrDataUrl };
                })
            );
        } catch(error){
            alert(`The tickets were saved, but their QR codes could not be generated. Try again and contact support if this happens again`)
            logError(`The tickets were saved, but their QR codes could not be generated. ${error.message}. payee: ${payeeId} payeeCatalogId: ${payeeCatalogId}`)
            dispatch(toggleLoading(false))
            return
        }
        dispatch(toggleLoading(false))
        setGeneratedTickets(ticketsWithQRDataUrls)  
        setShowTicketDownloadModal(true)
    }
    return (
        <>
        {
            !showTicketDownloadModal ?
            <div className="formContainer">
                <div className="formHeader">
                    <div className="h2 text-align-center">{title ? title : `Generate Tickets for ${catalogName}`}</div>
                </div>
                <div className="formBody">
                    <div className="label">1. Choose a product to generate tickets for</div>
                    <div className="item-list max-height-200px margin-bottom-1em">
                        {
                            ticketProductList.map(p => {
                                //find the selected product id using the selected variant id
                                const selectedProductId = productIdByProductVariantId[selectedProductVariantId]
                                //determine whether this product is selected
                                const selected = Boolean(p.id === selectedProductId)
                                return <ProductRadioListItem 
                                            key={p.id}
                                            product={p} 
                                            showDescription={false}
                                            selected={selected}
                                            defaultActiveVariantId={selected ? selectedProductVariantId : ""}
                                            //if the product is not the currently selected one, and it's checkbox is selected, 
                                            //then set the new variant id to its default variant
                                            onChange={() => {if (!selected) {
                                                    setSelectedVariantProductId(p.defaultVariantId)
                                                    setErrors({})
                                                }
                                            }}
                                            //if the variant is changed, and the product is selected
                                            //then set it to the selected variant
                                            onChangeActiveVariantId={variantId => {if (selected) setSelectedVariantProductId(variantId)}}

                                        />
                            })
                        }
                    </div>
                    {errors.variantId ? <div className="errorText">{errors.variantId}</div> : null}
                    <div className="margin-bottom-1em">
                        <FormField 
                            value={numToGenerate} 
                            label={`2. How many tickets would you like to generate${selectedProduct ? ` for ${productName} - ${variantDetails}`: ""}?`}
                            type="number"
                            onChange={e => handleChange({
                                e, 
                                type: "number",
                                min: 0,
                                max: 1000,
                                setValue: setNumToGenerate,
                                setErrors
                            })} //i want to do something like this
                            errorText={errors.numToGenerate}
                        />
                    </div>
                    <div>
                        <div className="label">3. Design your ticket</div>
                        {
                            displayTicket ?
                            <div>
                                <TicketDesigner 
                                    style={ticketStyle}
                                    onChange={setTicketStyle}
                                />
                                <div className="label margin-top-5px">Result:</div>
                                <TicketCard 
                                    ticket={displayTicket}
                                    objectType={OBJECT_TYPE_TICKET}
                                    style={ticketStyle}
                                />
                            </div>
                            :
                            null
                        }
                    </div>
                </div>
                <div className="formFooter">
                    <button className="button secondary width-100pct" onClick={handleGenerate}>Preview {numToGenerate ? numToGenerate : ""} Tickets</button>
                </div>
                <Modal
                    isOpen={generatedTickets && generatedTickets.length > 0}
                    closeModal={() => setGeneratedTickets([])}
                    showCloseButton={true}
                    className="height-100vh width-100pct"
                    overlayClassName="flex-center"
                    closeOnOverlayClick={true}
                >
                    <div className="formContainer">
                        <div className="formHeader h2 text-align-center">Preview {numToGenerate} Tickets</div>
                        <div className="formBody">
                            <TicketPrintPages 
                                tickets={generatedTickets}
                                ticketStyle={ticketStyle}
                            />
                        </div>
                        <div className="formFooter">
                            <button className="button secondary width-100pct" onClick={handleSaveTickets}>Save Tickets</button>
                        </div>
                    </div>
                </Modal>
            </div>
            :
            // display the download modal after the tickets have been saved
            <ErrorBoundary
                errorHeading="Failed to load PDF download modal"
                errorContext="PhysicalTicketGneeratorForm TicketPdfDownloadModal lazy-loading"
            >
                <Suspense fallback={<Loading />}>
                    <TicketPdfDownloadModal
                        isOpen={showTicketDownloadModal}
                        closeModal={() => {
                                setShowTicketDownloadModal(false)
                                onSuccess()
                            }
                        }
                        closeOnOverlayClick={false}
                        tickets={generatedTickets} 
                        fileName={ticketFilename}
                        objectType={OBJECT_TYPE_TICKET}
                        ticketStyle={ticketStyle}
                    />
                </Suspense>
            </ErrorBoundary>
        }
        </>
    )
}

export default PhysicalTicketGeneratorForm